import React, { memo, useCallback, useMemo, useState } from 'react';
import { View, TextInput, FlatList, TouchableOpacity, ActivityIndicator } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';

export interface SelectableItem {
    id: number | string;
    name: string;
}

interface SelectableListProps {
    selectableLabel: string;
    selectedLabel: string;
    selectableItems: SelectableItem[];
    selectedItems: SelectableItem[];
    onAddItem: (item: SelectableItem) => void;
    onRemoveItem: (item: SelectableItem) => void;
    onAddAll: () => void;
    onRemoveAll: () => void;
    onLoadMoreSelectable?: () => void;
    isLoadingMoreSelectable?: boolean;
    displayFilter?: string[];
    onSelectableSearchChange: (value: string) => void;
}

function SelectableListPaginated({
    selectableLabel,
    selectedLabel,
    selectableItems,
    selectedItems,
    onAddItem,
    onRemoveItem,
    onLoadMoreSelectable,
    isLoadingMoreSelectable = false,
    displayFilter,
    onSelectableSearchChange,
    onAddAll,
    onRemoveAll
}: SelectableListProps) {
    const { styles, theme } = useStyles(styleSheet);

    const [selectableTerm, setSelectableTerm] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');

    const filterList = useCallback(
        (items: SelectableItem[], term: string) => {
            const upper = term.toUpperCase();

            return items.filter(item =>
                item.name.toUpperCase().includes(upper) &&
                (!displayFilter || displayFilter.includes(String(item.id)))
            );
        },
        [displayFilter]
    );

    const filteredSelectable = useMemo(
        () => filterList(selectableItems, ''),
        [selectableItems, filterList]
    );

    const filteredSelected = useMemo(
        () => filterList(selectedItems, selectedTerm),
        [selectedItems, selectedTerm, filterList]
    );

    const renderSelectableItem = useCallback(
        ({ item }: { item: SelectableItem; }) => (
            <View style={{ gap: 5, paddingRight: 10 }}>
                <TouchableOpacity onPress={() => onAddItem(item)}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <MyAppText>{item.name}</MyAppText>
                        <FontAwesomeIcon color={theme.colors.labelColor} icon={faAngleRight} />
                    </View>
                </TouchableOpacity>
                <View style={styles.tableLine}></View>
            </View>
        ),
        [onAddItem]
    );

    const renderSelectedItem = useCallback(
        ({ item }: { item: SelectableItem; }) => (
            <View style={{ gap: 5, paddingRight: 10 }}>
                <TouchableOpacity onPress={() => onRemoveItem(item)}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <MyAppText>{item.name}</MyAppText>
                        <FontAwesomeIcon color={theme.colors.labelColor} icon={faAngleLeft} />
                    </View>
                </TouchableOpacity>
                <View style={styles.tableLine}></View>
            </View>
        ),
        [onRemoveItem]
    );

    return (
        <View style={styles.container}>
            <View style={{ flex: 1, rowGap: 5 }}>
                <MyAppText style={styles.tableHeaderText}>{selectableLabel}</MyAppText>
                <View style={styles.table}>
                    <TextInput
                        style={styles.input}
                        value={selectableTerm}
                        onChangeText={(text) => {
                            setSelectableTerm(text);
                            onSelectableSearchChange(text);
                        }}
                    />
                    <FlatList
                        data={filteredSelectable}
                        keyExtractor={(item) => String(item.id)}
                        renderItem={renderSelectableItem}
                        onEndReachedThreshold={0.6}
                        onEndReached={() => {
                            if (!onLoadMoreSelectable) {
                                return;
                            }
                            onLoadMoreSelectable();
                        }}
                        ListFooterComponent={
                            isLoadingMoreSelectable ? <ActivityIndicator /> : null
                        }
                    />
                </View>
            </View>

            <View style={{ justifyContent: 'center', rowGap: 10 }}>
                <TouchableOpacity onPress={onAddAll}>
                    <FontAwesomeIcon color={theme.colors.labelColor} icon={faAngleRight} fontSize={24} />
                </TouchableOpacity>

                <TouchableOpacity onPress={onRemoveAll}>
                    <FontAwesomeIcon color={theme.colors.labelColor} icon={faAngleLeft} fontSize={24} />
                </TouchableOpacity>
            </View>

            <View style={{ flex: 1, rowGap: 5 }}>
                <MyAppText style={styles.tableHeaderText}>{selectedLabel}</MyAppText>
                <View style={styles.table}>
                    <TextInput style={styles.input} onChangeText={setSelectedTerm} />
                    <FlatList
                        data={filteredSelected}
                        keyExtractor={(item) => String(item.id)}
                        renderItem={renderSelectedItem}
                    />
                </View>
            </View>
        </View >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'row',
        columnGap: 25,
        flex: 1
    },

    tableHeaderText: {
        color: theme.colors.labelColor,
        fontSize: 14,
        fontWeight: 'bold',
    },
    table: {
        paddingTop: 15,
        paddingLeft: 15,
        paddingRight: 15,
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 4,
        rowGap: 15,
        flex: 1
    },
    input: {
        backgroundColor: theme.colors.container.background,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
        height: 32,
        padding: 10,
        borderRadius: 4,
        color: theme.colors.field.text,
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
    },
}));


export const SelectablePaginatedList = memo(SelectableListPaginated);
